import { useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { aiService, type SOAPNote, type AICodeSuggestion, type ProcessingResult } from '../services/aiService';

// Key for React Query
const AI_DOCUMENTATION_KEY = ['ai', 'documentation'] as const;

/**
 * Hook for transcribing audio using Whisper API
 */
export function useTranscribeAudio() {
  return useMutation({
    mutationFn: async (audioBlob: Blob) => {
      return aiService.transcribeAudio(audioBlob);
    },
  });
}

/**
 * Hook for generating SOAP note from transcript using GPT-4
 */
export function useGenerateSOAPNote() {
  return useMutation({
    mutationFn: async ({ transcript, patientContext }: { transcript: string; patientContext?: string }) => {
      return aiService.generateSOAPNote(transcript, patientContext);
    },
  });
}

/**
 * Hook for suggesting ICD-10 and CPT codes based on SOAP note
 */
export function useSuggestCodes() {
  return useMutation({
    mutationFn: async (soapNote: SOAPNote) => {
      return aiService.suggestCodes(soapNote);
    },
  });
}

type ProcessConsultationVariables = { audioBlob: Blob; patientContext?: string };
type ProcessConsultationData = ProcessingResult;
export type ProcessConsultationError = Error & { code?: string };

/**
 * Options for useProcessConsultation hook
 */
export interface UseProcessConsultationOptions {
  onSuccess?: (data: ProcessConsultationData) => void;
  onError?: (error: ProcessConsultationError) => void;
  onSettled?: (data: ProcessConsultationData | undefined, error: ProcessConsultationError | null) => void;
}

/**
 * Main hook for processing a complete consultation (audio → transcript → SOAP → codes)
 */
export function useProcessConsultation(options?: UseProcessConsultationOptions) {
  const queryClient = useQueryClient();

  return useMutation<ProcessConsultationData, ProcessConsultationError, ProcessConsultationVariables>({
    mutationFn: async ({ audioBlob, patientContext }) => {
      return aiService.processConsultation(audioBlob, patientContext);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: AI_DOCUMENTATION_KEY });
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
    onSettled: (data, error) => {
      options?.onSettled?.(data ?? undefined, error ?? null);
    },
  });
}

/**
 * Hook state type for the full AI documentation flow
 */
export interface AIDocumentationState {
  // Recording state
  isRecording: boolean;
  recordingDuration: number;
  audioBlob: Blob | null;

  // Processing states
  isTranscribing: boolean;
  isGeneratingSOAP: boolean;
  isSuggestingCodes: boolean;

  // Results
  transcript: string | null;
  soapNote: SOAPNote | null;
  codes: AICodeSuggestion[];
  processingError: string | null;

  // Step tracking
  currentStep: 'idle' | 'recording' | 'transcribing' | 'generating_soap' | 'suggesting_codes' | 'complete';
}

/**
 * Result type for the useProcessConsultation hook
 */
export type ProcessConsultationResult = ProcessingResult;
